import React, { useContext, useEffect, useState } from 'react'
import { Box, Button, IconButton, Typography, styled, useTheme, SxProps, Theme, keyframes } from '@mui/material'
import SkillynxLogo from 'assets/logos/logo_bold.png'
import ImageComponent from './ImageComponent'
import useScreenSize from 'hooks/ScreenSize'
import MenuIcon from 'assets/icons/menu.png'
import MenuIconDark from 'assets/icons/Menu-Dark.png'
import { MobileWidth, TextSizes } from 'entities/constants'
import { useTranslation } from 'react-i18next'
import { TextSizeContext } from 'data/index'
import { DrawerPropType } from 'entities/interfaces'
import ScoreIcon from 'assets/icons/scoreIcon.png'
import FlameIcon from 'assets/icons/flame.png'
import { useNavigate } from 'react-router-dom'
import { POST } from 'api/index'
import { usePoints } from "context/PointsContext"
import SettingsModal from "ui/components/pages/settings/modals/SettingsModal"
import SettingsTabs from "ui/components/pages/settings/tabs/SettingsTabs"
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import MenuOutlinedIcon from '@mui/icons-material/Menu';
import { Drawer, List, ListItem, ListItemIcon, ListItemText, Avatar, Divider as MuiDivider } from '@mui/material';

const shine = keyframes`
  0%   { background-position: -60px 0; }
  100% { background-position: 60px 0;  }
`

interface ProgressProps {
  total: number
  done: number
  sx?: SxProps<Theme>
}

const CourseProgressBlocks: React.FC<ProgressProps> = ({ total, done, sx }) => {
  const theme = useTheme()

  const totalInt = Math.max(0, total)
  const doneInt = Math.max(0, Math.min(done, totalInt))
  const activeIdx = doneInt

  if (!totalInt) return null

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 0.75,
        flexWrap: 'nowrap',
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        ...sx,
      }}
    >
      {Array.from({ length: totalInt }).map((_, i) => {
        const isDone = i < doneInt
        const isActive = i === activeIdx && doneInt < totalInt

        return (
          <Box
            key={i}
            sx={{
              width: 36,
              height: 10,
              borderRadius: 2,
              flexShrink: 0,
              position: 'relative',

              ...(isDone && {
                backgroundColor: theme.palette.primary.main,
                border: `2px solid ${theme.palette.primary.main}`,
              }),

              ...(isActive && {
                border: `2px solid ${theme.palette.primary.main}`,
                background:
                  `linear-gradient(90deg,
                    ${theme.palette.primary.main} 0%,
                    ${theme.palette.primary.light} 50%,
                    ${theme.palette.primary.main} 100%)`,
                backgroundSize: '120px 100%',
                animation: `${shine} 1.2s linear infinite`,
              }),

              ...(!isDone && !isActive && {
                backgroundColor: theme.palette.common.white,
                border: `2px solid ${theme.palette.grey[700]}`,
              }),
            }}
          />
        )
      })}
    </Box>
  )
}

const HeaderBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  padding: '1rem 1.25rem 1rem 1rem',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  boxShadow: theme.palette.mode === 'light' ? '0px 4px 8px rgba(0, 0, 0, 0.1)' : '',
  zIndex: '1000',
  height: '5rem',
  position: 'fixed',
  top: 0,
  left: 0,
}));

// Desktop Styles
const StatsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flex: 1,
  minWidth: '16rem',
  width: 'fit-content',
  maxWidth: '18rem',
  alignItems: 'center',
  border: '2px solid #a3b3a3',
  borderRadius: '12px',
  overflow: 'hidden',
  height: '3.5rem',
  padding: '0.2rem 0.5rem',
  flexShrink: 0
}));

const StatBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '50%',
  gap: '0.5rem',
  padding: '0.3rem 0.8rem',
  backgroundColor: theme.palette.common.white,
  transition: 'transform 0.2s, box-shadow 0.2s',
  cursor: 'pointer',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)',
    backgroundColor: theme.palette.grey[100],
  },
}));

const Divider = styled(Box)(({ theme }) => ({
  width: '2px',
  backgroundColor: '#a3b3a3',
  height: '80%',
  margin: '0 0.5rem',
}));

const TitleProgressBox = styled(Box)(({ theme }) => ({
  marginTop: '0.8rem'
}));

const ProgressBox = styled(Box)(({ theme }) => ({
  marginLeft: '2.8em',
}));

const TitleBox = styled(Box)(({ theme }) => ({
  marginLeft: '3rem',
  display: 'flex',
  alignItems: 'center',
  height: '100%',
  flexGrow: '1',
  flexBasis: 0,
  gap: '1rem'
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: '2.5rem',
  height: '2.5rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const CoinIconWrapper = styled(Box)(({ theme }) => ({
  width: '2.5rem',
  height: '2.5rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StatText = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: '700',
  color: theme.palette.grey[700],
}));

// Mobile Styles
const MobileStatsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
}));

const MobileStatItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.5rem',
  borderRadius: '8px',
  backgroundColor: theme.palette.grey[50],
  border: `1px solid ${theme.palette.grey[200]}`,
  transition: 'transform 0.2s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  },
}));

const MobileIconWrapper = styled(Box)(({ theme }) => ({
  width: '1.5rem',
  height: '1.5rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const MobileStatText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: '600',
  color: theme.palette.grey[700],
  minWidth: 'fit-content',
}));

// Mobile Menu Styles (entspricht dem Wireframe)
const MobileMenuDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: '280px',
    backgroundColor: theme.palette.common.white,
    boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)',
    border: 'none',
    padding: 0,
  },
}));

const MenuHeader = styled(Box)(({ theme }) => ({
  padding: '2rem 1.5rem 1.5rem',
  backgroundColor: theme.palette.common.white,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '1rem',
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: '80px',
  height: '80px',
  backgroundColor: '#f6c767',
  fontSize: '2rem',
  fontWeight: '600',
  color: theme.palette.common.white,
}));

const UserName = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: '600',
  color: theme.palette.common.black,
  textAlign: 'center',
}));

const UserEmail = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.grey[600],
  textAlign: 'center',
}));

const MenuList = styled(List)(({ theme }) => ({
  padding: '0.5rem 0',
  '& .MuiListItem-root': {
    padding: '0.75rem 1.5rem',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.grey[50],
    },
    '&.logout': {
      color: theme.palette.error.main,
      '&:hover': {
        backgroundColor: theme.palette.error.light + '10',
      },
      '& .MuiListItemIcon-root': {
        color: theme.palette.error.main,
      },
      '& .MuiListItemText-primary': {
        color: theme.palette.error.main,
      },
    },
  },
}));

const MenuItemIcon = styled(ListItemIcon)(({ theme }) => ({
  minWidth: '40px',
  color: theme.palette.grey[600],
}));

const MenuItemText = styled(ListItemText)(({ theme }) => ({
  '& .MuiListItemText-primary': {
    fontSize: '1rem',
    fontWeight: '500',
    color: theme.palette.common.black,
  },
}));

// Mobile Menu Component
interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
  profileImage?: string | null;
}

const MobileMenuComponent: React.FC<MobileMenuProps> = ({ open, onClose, profileImage }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const currentUserName = localStorage.getItem("currentUserName") || "";
  const currentUserEmail = localStorage.getItem("currentUserEmail") || "";

  const handleLogout = () => {
    localStorage.removeItem('currentUserToken');
    localStorage.removeItem('currentUserName');
    localStorage.removeItem('currentUserEmail');
    onClose();
    navigate('/auth');
  };

  const handleMenuItemClick = (path: string) => {
    onClose();
    navigate(path);
  };

  const menuItems = [
    {
      id: 'personal',
      label: t("Personal"),
      icon: <PersonOutlineIcon />,
      path: '/mobile/settings/personal',
    },
    {
      id: 'learning',
      label: t("Learning Preferences"),
      icon: <SchoolOutlinedIcon />,
      path: '/mobile/settings/learning',
    },
    {
      id: 'avatar',
      label: t("Avatar Selection"),
      icon: <AccountCircleOutlinedIcon />,
      path: '/mobile/settings/avatar',
    },
    {
      id: 'notifications',
      label: t("Notifications"),
      icon: <NotificationsNoneOutlinedIcon />,
      path: '/mobile/settings/notifications',
    },
    {
      id: 'security',
      label: t("Security"),
      icon: <SecurityOutlinedIcon />,
      path: '/mobile/settings/security',
    },
    {
      id: 'feedback',
      label: t("Feedback"),
      icon: <FeedbackOutlinedIcon />,
      path: '/mobile/settings/feedback',
    },
    {
      id: 'impressum',
      label: t("Imprint & Privacy"),
      icon: <ArticleOutlinedIcon />,
      path: '/mobile/settings/privacy',
    },
  ];

  return (
    <MobileMenuDrawer
      anchor="left"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
      }}
    >
      {/* Header mit Benutzerinfo */}
      <MenuHeader>
        {profileImage ? (
          <Avatar
            src={profileImage}
            sx={{
              width: '80px',
              height: '80px',
            }}
          />
        ) : (
          <UserAvatar>
            {currentUserName.charAt(0).toUpperCase()}
          </UserAvatar>
        )}
        <Box sx={{ textAlign: 'center' }}>
          <UserName>{currentUserName}</UserName>
          <UserEmail>{currentUserEmail}</UserEmail>
        </Box>
      </MenuHeader>

      {/* Menu Items */}
      <MenuList>
        {menuItems.map((item) => (
          <ListItem
            key={item.id}
            onClick={() => handleMenuItemClick(item.path)}
          >
            <MenuItemIcon>
              {item.icon}
            </MenuItemIcon>
            <MenuItemText primary={t(item.label)} />
          </ListItem>
        ))}

        <MuiDivider sx={{ margin: '0.5rem 1.5rem' }} />

        {/* Logout Item */}
        <ListItem
          className="logout"
          onClick={handleLogout}
        >
          <MenuItemIcon>
            <LogoutOutlinedIcon />
          </MenuItemIcon>
          <MenuItemText primary={t('Abmelden')} />
        </ListItem>
      </MenuList>
    </MobileMenuDrawer>
  );
};

const Header = ({
  data,
  isSetting = false,
  totalScore,
  currentStreak,
  chapterTitle,
  chapterImage,
}: {
  data?: DrawerPropType,
  isSetting?: boolean,
  totalScore?: number,
  currentStreak?: number,
  chapterTitle?: string,
  chapterImage?: string,
}) => {
  const navigate = useNavigate();
  const { width } = useScreenSize();
  const { t } = useTranslation();
  const theme = useTheme();
  const { state: textSize } = useContext(TextSizeContext);
  const [streak, setStreak] = useState<number>(0);
  const { totalPoints } = usePoints();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [userData, setUserData] = useState<any>(null);
  const [chaptersDone, setChaptersDone] = useState<number | null>(null);
  const [chaptersTotal, setChaptersTotal] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const getUserData = async () => {
    const token = localStorage.getItem('currentUserToken');
    if (!token) return;

    const body = new URLSearchParams();
    body.append('userToken', token);

    try {
      const response = await POST('getUserPersonalData', body);
      const result = response.content;
      setUserData(result);

      // Profilbild direkt setzen, falls vorhanden
      if (result?.image) {
        setProfileImage(result.image);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Benutzerdaten:', error);
    }
  };

  useEffect(() => {
    if (!profileImage && !userData) {
      getUserData();
    }
  }, []);

useEffect(() => {
  const updateProfileImage = () => {
    // Zuerst localStorage prüfen
    const newImage = localStorage.getItem("currentUserImage");
    if (newImage) {
      setProfileImage(newImage);
    } else {
      // Falls localStorage leer ist, API-Daten neu laden
      getUserData();
    }
  };

  updateProfileImage();
  window.addEventListener("profileImageChanged", updateProfileImage);

  return () => {
    window.removeEventListener("profileImageChanged", updateProfileImage);
  };
}, []);

  useEffect(() => {
    const courseId = data?.id
    if (!courseId) return

    const fetchProgress = async () => {
      const body = new URLSearchParams()
      body.append('userToken', localStorage.getItem('currentUserToken') ?? '')
      body.append('option', courseId.toString())
      const { result } = await POST('getCourseProgress', body)
      setChaptersTotal(Number(result.total_chapters) || 0)
      setChaptersDone(Number(result.completed_chapters) || 0)
    }

    fetchProgress()
  }, [chapterTitle, data?.id]);

  const getUserStreak = async () => {
    const body = new URLSearchParams();
    body.append('userToken', localStorage.getItem('currentUserToken') || '');
    const response = await POST('getUserStreak', body);
    if (response.status === 200) {
      setStreak(response.streak);
    }
  };

  function formatValue(value: number): string {
    return value.toLocaleString('de-DE');
  }

  useEffect(() => {
    const fetchAndUpdateStreak = async () => {
      const userToken = localStorage.getItem('currentUserToken');
      if (!userToken) return;
      const body = new URLSearchParams();
      body.append('userToken', userToken);
      const updateResponse = await POST('setUserStreak', body);
      getUserStreak();
    };

    fetchAndUpdateStreak();
  }, []);


  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      <HeaderBox>
        {width > MobileWidth ? (
          // Desktop Layout
          <>
            {/* Logo */}
            <Box sx={{ width: '16.62rem', paddingTop: '0.4rem', marginLeft: '2rem' }}>
              <ImageComponent src={SkillynxLogo} alt='Logo' height='43px' width='158.052px' />
            </Box>

            {/* Titel */}
            <TitleProgressBox sx={{ flexGrow: 1 }}>
              <TitleBox>
                {data?.title && (
                  <>
                    <ImageComponent
                      src={theme.palette.mode === 'dark' && data.thumbnailDark ? data.thumbnailDark : data.thumbnail}
                      alt='skill'
                      width={isSetting ? '3rem' : '3.75rem'}
                      height={isSetting ? '3rem' : '3.75rem'}
                      borderRadius={isSetting ? '' : '50%'}
                    />
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: TextSizes[textSize].title3,
                        color: theme.palette.common.black,
                      }}
                    >
                      {t(data.title)}
                    </Typography>
                    {chapterImage && (
                      <img
                        src={chapterImage}
                        alt=""
                        style={{ width: '2.5rem', height: '2.5rem', borderRadius: '8px' }}
                      />
                    )}
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: TextSizes[textSize].title3,
                        color: theme.palette.common.black,
                      }}
                    >
                      {chapterTitle}
                    </Typography>
                  </>
                )}
              </TitleBox>
              {chaptersDone !== null && chaptersTotal !== null && (
                <ProgressBox>
                  <CourseProgressBlocks done={chaptersDone} total={chaptersTotal} sx={{ mb: '0.6rem', ml: '5rem' }} />
                </ProgressBox>
              )}
            </TitleProgressBox>

            {/* Streak & Score */}
            <StatsContainer>
              <StatBox>
                <IconWrapper>
                  <ImageComponent src={FlameIcon} alt="Streak Icon" width="100%" height="100%" />
                </IconWrapper>
                <StatText>{streak}</StatText>
                <StatText>{currentStreak}</StatText>
              </StatBox>

              <Divider />

              <StatBox>
                <CoinIconWrapper>
                  <ImageComponent src={ScoreIcon} alt="Score Icon" width="100%" height="100%" />
                </CoinIconWrapper>
                <StatText>{formatValue(Number(totalPoints))}</StatText>
              </StatBox>
            </StatsContainer>

            {/* Profil-Icon */}
            {profileImage ? (
              <IconButton
                onClick={() => setSettingsOpen(true)}
                sx={{
                  width: "3rem",
                  height: "3rem",
                  borderRadius: "50%",
                  overflow: "hidden",
                  marginLeft: "5rem",
                  padding: 0,
                }}
              >
                <img
                  src={profileImage}
                  alt="Profilbild"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </IconButton>
            ) : (
              <Box
                onClick={() => setSettingsOpen(true)}
                sx={{
                  backgroundColor: "#f6c767",
                  width: "3rem",
                  height: "3rem",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "1.5rem",
                  marginLeft: "8rem",
                  cursor: "pointer",
                  transition: "transform 0.2s ease-in-out",
                  '&:hover': {
                    transform: "scale(1.1)",
                  },
                }}
              >
                {window.localStorage.getItem("currentUserName")?.charAt(0)}
              </Box>
            )}
          </>
        ) : (
          // Mobile Layout
          <>
            {/* Hamburger Menu */}
            <IconButton onClick={handleMobileMenuToggle}>
              <MenuOutlinedIcon sx={{ fontSize: '1.5rem', color: theme.palette.common.black }} />
            </IconButton>

            {/* Logo */}
            <ImageComponent src={SkillynxLogo} alt='Logo' height='2rem' width='7.1875rem' />

            {/* Mobile Stats */}
            <MobileStatsContainer>
              {/* Streak */}
              <MobileStatItem>
                <MobileIconWrapper>
                  <ImageComponent src={FlameIcon} alt="Streak Icon" width="100%" height="100%" />
                </MobileIconWrapper>
                <MobileStatText>{streak}</MobileStatText>
              </MobileStatItem>

              {/* Points */}
              <MobileStatItem>
                <MobileIconWrapper>
                  <ImageComponent src={ScoreIcon} alt="Score Icon" width="100%" height="100%" />
                </MobileIconWrapper>
                <MobileStatText>{formatValue(Number(totalPoints))}</MobileStatText>
              </MobileStatItem>
            </MobileStatsContainer>
          </>
        )}
      </HeaderBox>

      {/* Mobile Menu Drawer */}
      <MobileMenuComponent
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        profileImage={profileImage}
      />

      {/* Settings Modal (Desktop) */}
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} title={t("Einstellungen")}>
        <SettingsTabs onClose={() => setSettingsOpen(false)} />
      </SettingsModal>
    </>
  );
};

export default Header;